import { useEffect, useState } from "react";
import { Auth } from "../auth/auth";
import { Button } from "../../../shared/ui/button";
import { Title } from "../../../shared/ui/title";
import { Modal } from "../../../shared/ui/modal";
import "./unsubscribe.scss";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import { selectUser } from "../../data-access/store/authSlice";
import { formatDate } from "../../../utils/dates";
import {
  selectInit,
  selectLanguage,
  selectTranslations,
} from "../../../shared/data-access/store/general/generalSlice";
import {
  useLazyGetUserQuery,
  useUnsubscribeMutation,
} from "../../data-access/store/services/auth.service";
import { setMessage } from "../../../shared/data-access/store/message/messageSlice";
import { useNavigate } from "react-router-dom";
import { checkTranslationKey } from "../../../utils/translation";

export function Unsubscribe() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const init = useAppSelector(selectInit);
  const user = useAppSelector(selectUser);
  const [getUser] = useLazyGetUserQuery();
  const [showModalText, setShowModalText] = useState<boolean>(false);
  const [requestSubmitted, setRequestSubmitted] = useState<boolean>(false);
  const unsubscribeMethod = init?.users?.unsubscribeMethod;
  const [
    unsubscribe,
    { error: unsubscribeError, isSuccess: unsubscribeIsSuccess },
  ] = useUnsubscribeMutation();
  const lang = useAppSelector(selectLanguage);
  const translations = useAppSelector(selectTranslations);

  const expireDate = user?.eligibility?.expires
    ? formatDate(user.eligibility.expires)
    : "";
  const renewalDate = user?.eligibility?.renewal
    ? formatDate(user.eligibility.renewal)
    : "";

  useEffect(() => {
    if (unsubscribeError) {
      setRequestSubmitted(false);
      const errorMsg =
        (unsubscribeError as { data?: Error })?.data?.message ||
        checkTranslationKey(translations[lang]?.Error, "Error!");
      if (errorMsg) {
        dispatch(setMessage({ content: errorMsg, type: "error" }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unsubscribeError]);

  useEffect(() => {
    if (unsubscribeIsSuccess) {
      getUser();
    }
  }, [unsubscribeIsSuccess, getUser]);

  const handleUnsubscribe = () => {
    unsubscribe();
    setRequestSubmitted(true);
  };

  const handleGoToHomepage = () => {
    navigate("/");
  };

  const handleCancel = () => {
    navigate("/profile");
  };

  const isSubscribed = user?.eligibility?.subscriptionStatus === "ACTIVE";
  const confirmPrompt =
    unsubscribeMethod === "direct"
      ? checkTranslationKey(
          translations[lang]
            ?.Confirm_below_if_you_wish_to_stop_your_subscription,
          "Confirm below if you wish to stop your subscription."
        )
      : checkTranslationKey(
          translations[lang]?.To_cancel_your_subscription_send_and_SMS_to_4124,
          "To cancel your subscription send and SMS to 4124"
        );

  return (
    <div id="unsubscribe">
      <Auth
        className="auth-shell-content"
        header={
          <Title backURL="/profile">
            {checkTranslationKey(
              translations[lang]?.Unsubscribe,
              "Unsubscribe"
            )}
          </Title>
        }
        hasFooter={false}
      >
        {requestSubmitted ? (
          <div className="processing-view">
            <p className="processing-title">
              {checkTranslationKey(
                translations[lang]?.Your_request_is_under_process,
                "Your request is under process."
              )}
            </p>
            <p className="processing-subtitle">
              {checkTranslationKey(
                translations[lang]?.We_re_sorry_to_see_you_go,
                "We are sorry to see you go!"
              )}
            </p>
            <Button
              buttonType="success"
              type="button"
              onClick={handleGoToHomepage}
            >
              {checkTranslationKey(translations[lang]?.Go_to_Home, "Homepage")}
            </Button>
          </div>
        ) : (
          <>
            <div
              className="subscription-info-item"
              style={{ borderTop: "unset" }}
            >
              <p>
                {checkTranslationKey(
                  translations[lang]?.Eligible_until,
                  "Eligible until"
                )}
              </p>
              <span className="tag-item flex items-center">
                <span>{expireDate || "-------"}</span>
              </span>
            </div>
            <div className="subscription-info-item">
              <p>
                {checkTranslationKey(
                  translations[lang]?.Renewal_date,
                  "Renewal date"
                )}
              </p>
              <span className="tag-item flex items-center">
                {renewalDate || "-------"}
              </span>
            </div>
            <div
              className="subscription-info-item"
              style={{ borderBottom: "unset" }}
            >
              <p>
                {checkTranslationKey(
                  translations[lang]?.Subscription_scheme,
                  "Subscription scheme"
                )}
              </p>
              <span className="tag-item flex items-center">
                {init?.users?.subscriptionType}
              </span>
            </div>
            <div className="btn-container">
              <p className="confirm-prompt">{confirmPrompt}</p>
              <Button
                disabled={!isSubscribed}
                buttonType="success"
                type="submit"
                value="Submit"
                onClick={() =>
                  unsubscribeMethod === "direct"
                    ? handleUnsubscribe()
                    : setShowModalText(true)
                }
              >
                {isSubscribed
                  ? checkTranslationKey(
                      translations[lang]?.Unsubscribe_Button,
                      "Confirm"
                    )
                  : checkTranslationKey(
                      translations[lang]?.Already_unsubscribed,
                      "Already unsubscribed"
                    )}
              </Button>
              <Button
                buttonType="white"
                type="submit"
                value="Cancel"
                onClick={handleCancel}
                style={{ marginTop: "15px" }}
              >
                {checkTranslationKey(translations[lang]?.Cancel, "Cancel")}
              </Button>
            </div>
          </>
        )}

        <Modal setShow={setShowModalText} show={showModalText} hideCloseButton>
          <div className="modal-content">
            <div className="modal-description">
              <p className="title-modal">
                {checkTranslationKey(
                  translations[lang]?.We_re_sorry_to_see_you_go,
                  "We're sorry to see you go!"
                )}
              </p>
              <div className="description">
                <img src="/img/icns/unsubscribe.svg" alt="" />
                <p>
                  {checkTranslationKey(
                    translations[lang]
                      ?.To_cancel_your_subscription_send_and_SMS_to_4124,
                    "To cancel your subscription send and SMS to 4124"
                  )}
                </p>
              </div>
            </div>
            <div className="bottom-area">
              <Button
                onClick={() => setShowModalText(false)}
                buttonType="success"
                small
              >
                {checkTranslationKey(translations[lang]?.OK, "OK")}
              </Button>
            </div>
          </div>
        </Modal>
      </Auth>
    </div>
  );
}
